import { create } from 'zustand';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export interface AuthUser {
  user_id: string;
  email: string;
  zpc_id: string;
  role: string;
  company_name: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: { code: string; message: string; details?: { field: string; message: string }[] } | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  signup: (email: string, password: string, companyName: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  clearError: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>()((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (email: string, password: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        const detail = data.detail ?? data;
        set({
          isLoading: false,
          error: {
            code: detail.error ?? 'unknown',
            message: detail.message ?? 'Login failed',
            details: detail.details,
          },
        });
        return false;
      }

      set({
        user: {
          user_id: data.user_id,
          email,
          zpc_id: data.zpc_id,
          role: data.role,
          company_name: '',
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return true;
    } catch {
      set({
        isLoading: false,
        error: { code: 'network_error', message: 'Unable to connect. Please try again.' },
      });
      return false;
    }
  },

  signup: async (email: string, password: string, companyName: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, company_name: companyName }),
      });
      const data = await res.json();

      if (!res.ok) {
        const detail = data.detail ?? data;
        set({
          isLoading: false,
          error: {
            code: detail.error ?? 'unknown',
            message: detail.message ?? 'Signup failed',
            details: detail.details,
          },
        });
        return false;
      }

      set({
        user: {
          user_id: data.user_id,
          email,
          zpc_id: data.zpc_id,
          role: 'board_member',
          company_name: companyName,
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return true;
    } catch {
      set({
        isLoading: false,
        error: { code: 'network_error', message: 'Unable to connect. Please try again.' },
      });
      return false;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Logout should succeed even if API is unreachable
    }
    set({ user: null, isAuthenticated: false, error: null });
  },

  fetchMe: async (): Promise<void> => {
    set({ isLoading: true });
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
      });

      if (res.status === 401) {
        set({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      if (!res.ok) {
        set({ isLoading: false });
        return;
      }

      const data = await res.json();
      set({
        user: {
          user_id: data.user_id,
          email: data.email,
          zpc_id: data.zpc_id,
          role: data.role,
          company_name: data.company_name,
        },
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
  clearAuth: () => set({ user: null, isAuthenticated: false, error: null }),
}));
